import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fmtCurrencyBR } from '../../../utils/formatBrl';
import { TableSkeleton } from "../../../components/SkeletonLoading/PageSkeletons";
import ConversionCell from "../../../components/UI/ConversionCell";
import { fmtDate } from '../../utils/dateUtils/fmtDate';
import { useBotPhotosByUsername } from '../../../hooks/useBotPhotos';
import BotAvatar from '../../../components/BotAvatar/BotAvatar';
import {
    Play, Pause, CalendarClock, CalendarX2, Pencil, BarChart2,
    Send, DollarSign, Clock, Zap, CalendarCheck, Trash2, Sparkles, Repeat, Loader2,
    MoreVertical,
} from 'lucide-react';

const DAYS_SHORT = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function utcToBrt(t) {
    const [h, m] = t.split(':').map(Number);
    return `${String((h - 3 + 24) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatScheduleInfo(ds) {
    const { recurrence_type, recurrence_time, recurrence_days, recurrence_hours, recurrence_interval_hours, schedule } = ds;
    if (recurrence_type === 'daily')
        return `Diário às ${utcToBrt(recurrence_time)} (BRT)`;
    if (recurrence_type === 'interval')
        return `A cada ${recurrence_interval_hours}h`;
    if (recurrence_type === 'weekly') {
        // Se UTC hour < 3, o dia UTC foi shiftado +1 em relação ao dia BRT — reverter
        const utcHour = parseInt((recurrence_time || '00:00').split(':')[0]);
        const shifted = utcHour < 3;
        const brtDays = shifted
            ? (recurrence_days || []).map((d) => (d === 1 ? 7 : d - 1))
            : (recurrence_days || []);
        const days = brtDays.map((d) => DAYS_SHORT[d]).join(', ');
        return `Semanal: ${days} às ${utcToBrt(recurrence_time)} (BRT)`;
    }
    if (recurrence_type === 'custom') {
        const hours = (recurrence_hours || []).map(utcToBrt).join(', ');
        return `Personalizado: ${hours} (BRT)`;
    }
    if (schedule) {
        const d = new Date(schedule);
        const brt = new Date(d.getTime() - 3 * 60 * 60 * 1000);
        return `${brt.toLocaleDateString('pt-BR')} às ${brt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} (BRT)`;
    }
    return null;
}

function hasActiveSchedule(ds) {
    return !!ds.schedule || !!ds.recurrence_type;
}

function canScheduleDirectly(ds) {
    return ds.status === 'concluído' && !ds.recurrence_type;
}



function StatusBadge({ ds }) {
    const { status } = ds;
    // dispatching tem prioridade sobre qualquer agendamento
    if (status === 'disparando...') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 border border-blue-500/20 text-blue-400">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            Disparando
        </span>
    );
    if (hasActiveSchedule(ds)) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/10 border border-purple-500/20 text-purple-400">
            {ds.recurrence_type ? <Repeat size={10} /> : <Clock size={10} />}
            {ds.recurrence_type ? 'Recorrente' : 'Agendado'}
        </span>
    );
    if (status === 'concluído') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 border border-green-500/20 text-green-400">
            Concluído
        </span>
    );
    if (status === 'erro') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 border border-red-500/20 text-red-400">
            Erro
        </span>
    );
    if (status === 'aguardando') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 border border-yellow-500/20 text-yellow-400">
            Aguardando
        </span>
    );
    if (status === 'interrompido') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/10 border border-orange-500/20 text-orange-400">
            Interrompido
        </span>
    );
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/[0.06] text-white/40">
            {status || 'Rascunho'}
        </span>
    );
}

function RowMenu({ onEdit, onStats, onDelete, editDisabled = false, deleteDisabled = false, mobile = false }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const MENU_W = 176; // w-44

    const place = () => {
        const b = btnRef.current?.getBoundingClientRect();
        if (!b) return;
        setCoords({ top: b.bottom + 4, left: b.right - MENU_W });
    };

    const toggle = () => {
        if (!open) place();
        setOpen((v) => !v);
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const reposition = () => setOpen(false);
        document.addEventListener("mousedown", handler);
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            document.removeEventListener("mousedown", handler);
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open]);

    const item = (icon, label, fn, danger, disabled) => (
        <button
            onClick={() => { if (disabled) return; setOpen(false); fn(); }}
            disabled={disabled}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed ${danger ? "text-[#f87171]" : "text-white/80"}`}
        >
            {icon}{label}
        </button>
    );

    return (
        <div className={mobile ? "relative flex-1" : "relative"}>
            <button
                ref={btnRef}
                className={[
                    "action-btn",
                    mobile ? "action-btn--mobile action-btn--labeled w-full" : "",
                ].filter(Boolean).join(" ")}
                onClick={toggle}
                aria-label="Mais opções"
                title="Mais opções"
            >
                <MoreVertical size={mobile ? 18 : 14} />
                {mobile && <span>Mais</span>}
            </button>
            {open && coords && createPortal(
                <div
                    ref={menuRef}
                    style={{ position: "fixed", top: coords.top, left: coords.left, width: MENU_W }}
                    className="rounded-xl border border-white/[0.1] bg-[#161b24] shadow-[0_16px_40px_rgba(0,0,0,0.5)] py-1 z-[999]"
                >
                    {item(<Pencil size={14} />, "Editar", onEdit, false, editDisabled)}
                    {item(<BarChart2 size={14} />, "Histórico", onStats, false, false)}
                    <div className="my-1 h-px bg-white/[0.08]" />
                    {item(<Trash2 size={14} />, "Arquivar", onDelete, true, deleteDisabled)}
                </div>,
                document.body
            )}
        </div>
    );
}

function ActionButton({ onClick, title, icon: Icon, variant = "", disabled = false, mobile = false, text = "" }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={[
                "action-btn",
                variant,
                mobile ? "action-btn--mobile" : "",
                text ? "action-btn--labeled" : "",
                mobile && text ? "flex-1" : "",
            ].filter(Boolean).join(" ")}
        >
            <Icon size={mobile ? 18 : 14} />
            {text && <span>{text}</span>}
        </button>
    );
}

function SummaryCard({ icon: Icon, label, value, iconColor = "purple", change }) {
    return (
        <div className="stat-card">
            <div className="stat-card-header">
                <div className={`stat-icon ${iconColor}`}>
                    <Icon size={16} />
                </div>
                <div className="stat-title">{label}</div>
            </div>
            <div className="stat-value">{value}</div>
            {change && <div className="stat-change">{change}</div>}
        </div>
    );
}

export default function DisparoList({
    disparos,
    bots = [],
    loading,
    onEdit,
    onStart,
    onStop,
    onSchedule,
    onCancelSchedule,
    onStats,
    onDelete,
    pagination,
    currentPage,
    onNextPage,
    onPrevPage,
}) {
    const photoByUsername = useBotPhotosByUsername(bots);

    if (loading) {
        return (
            <div className="mt-6"><TableSkeleton label="Carregando disparos" columns={["Nome", "Bots", "Faturamento", "Conversão", "Status", "Última execução", "Ações"]} /></div>
        );
    }

    const countActive = disparos.filter((d) => d.status === "disparando...").length;
    const countScheduled = disparos.filter((d) => !!d.schedule).length;
    const totalSent = disparos.reduce((s, d) => s + (d.count_user_sent ?? 0), 0);
    const totalRevenue = disparos.reduce((s, d) => s + (d.total_value ?? 0), 0);

    return (
        <div className="mt-6">


            {/* Stats summary */}
            {/* {disparos.length > 0 && (
                <div className="stats-grid mb-6">
                    <SummaryCard icon={Send} label="Total Enviados" value={totalSent} iconColor="green" />
                    <SummaryCard icon={Zap} label="Disparando Agora" value={countActive} iconColor="blue" />
                    <SummaryCard icon={CalendarCheck} label="Agendados" value={countScheduled} iconColor="purple" />
                    <SummaryCard icon={DollarSign} label="Faturamento" value={fmtCurrencyBR.format(totalRevenue)} iconColor="orange" />
                </div>
            )} */}

            {disparos.length === 0 && (
                <div className="empty-state">
                    <Send />
                    <h4>Nenhum disparo criado</h4>
                    <p>Crie um novo disparo para recuperar vendas.</p>
                </div>
            )}

            {/* DESKTOP */}
            {disparos.length > 0 && (
                <div className="sx-desktop spt-reveal">
                    <table className="sx-table">
                        <thead>
                            <tr>
                                <th className="w-[200px]">Nome</th>
                                <th className="w-[350px]">Bots</th>
                                <th>Faturamento</th>
                                <th>Conversão</th>
                                <th>Status</th>
                                <th>Última execução</th>
                                <th className="w-[230px]">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {disparos.map((ds) => {
                                const isSending = ds.status === 'disparando...';
                                const activeSchedule = hasActiveSchedule(ds);
                                const needsCancelFirst = activeSchedule && !canScheduleDirectly(ds);
                                const scheduleInfo = formatScheduleInfo(ds);
                                return (
                                    <tr key={ds.id}>
                                        <td className="w-[200px] max-w-[200px]">
                                            <div className="truncate font-medium" title={ds.name}>{ds.name}</div>
                                            {scheduleInfo && (
                                                <div className="text-[10px] text-purple-400/70 mt-0.5 truncate" title={scheduleInfo}>
                                                    {scheduleInfo}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex flex-wrap gap-1.5">
                                                {(ds.bot_ids || []).map((bid) => {
                                                    const bot = bots.find((b) => b.id === bid);
                                                    return bot ? (
                                                        <BotAvatar
                                                            key={bid}
                                                            photoUrl={photoByUsername[bot.bot_username]}
                                                            size={32}
                                                            title={bot.bot_username}
                                                        />
                                                    ) : null;
                                                })}
                                                {!(ds.bot_ids?.length) && <span className="text-white/20 text-xs">—</span>}
                                            </div>
                                        </td>
                                        <td className="text-green-400">{fmtCurrencyBR.format(ds.total_value ?? 0)}</td>
                                        <td>
                                            <ConversionCell generated={ds.payments_generated ?? 0} paid={ds.payments_paid ?? 0} />
                                        </td>
                                        <td>
                                            <StatusBadge ds={ds} />
                                        </td>
                                        <td className="text-white/40 text-sm">{ds.last_run_at ? fmtDate(ds.last_run_at) : '—'}</td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1.5">
                                                {!needsCancelFirst && (
                                                    <ActionButton
                                                        icon={isSending ? Pause : Play}
                                                        title={isSending ? "Pausar" : "Iniciar"}
                                                        text={isSending ? "Pausar" : "Iniciar"}
                                                        onClick={() => isSending ? onStop(ds) : onStart(ds)}
                                                        disabled={activeSchedule && !isSending}
                                                        variant={isSending ? "action-btn--yellow" : "action-btn--green"}
                                                    />
                                                )}
                                                {needsCancelFirst ? (
                                                    <button
                                                        onClick={() => onCancelSchedule(ds)}
                                                        disabled={isSending}
                                                        className="action-btn action-btn--red action-btn--labeled"
                                                        title="Cancelar agendamento"
                                                    >
                                                        <CalendarX2 size={14} />
                                                        <span>Cancelar</span>
                                                    </button>
                                                ) : (
                                                    <ActionButton
                                                        icon={CalendarClock}
                                                        title="Agendar"
                                                        text="Agendar"
                                                        onClick={() => onSchedule(ds)}
                                                        disabled={isSending}
                                                        variant="action-btn--blue"
                                                    />
                                                )}
                                                <RowMenu
                                                    onEdit={() => onEdit(ds)}
                                                    onStats={() => onStats(ds)}
                                                    onDelete={() => onDelete(ds)}
                                                    editDisabled={isSending}
                                                    deleteDisabled={isSending}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MOBILE */}
            <div className="sx-mobile spt-reveal">
                {disparos.map((ds) => {
                    const isSending = ds.status === 'disparando...';
                    const activeSchedule = hasActiveSchedule(ds);
                    const needsCancelFirst = activeSchedule && !canScheduleDirectly(ds);
                    const scheduleInfo = formatScheduleInfo(ds);
                    const pct = ds.payments_generated > 0
                        ? ((ds.payments_paid / ds.payments_generated) * 100).toFixed(1)
                        : '0.0';
                    return (
                        <div key={ds.id} className="sx-card">
                            {/* Header: nome + menu (canto superior direito) */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="min-w-0">
                                    <div className="font-semibold text-white truncate" title={ds.name}>{ds.name}</div>
                                    {scheduleInfo && (
                                        <div className="text-[10px] text-purple-400/70 mt-0.5">{scheduleInfo}</div>
                                    )}
                                </div>
                                <RowMenu
                                    onEdit={() => onEdit(ds)}
                                    onStats={() => onStats(ds)}
                                    onDelete={() => onDelete(ds)}
                                    editDisabled={isSending}
                                    deleteDisabled={isSending}
                                />
                            </div>

                            {/* Stats: Faturamento, Conversão, Status, Bots */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                <div className="rounded-lg px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                    <div className="text-base font-bold text-green-400">{fmtCurrencyBR.format(ds.total_value ?? 0)}</div>
                                    <div className="text-[10px] text-white/35 mt-0.5">Faturamento</div>
                                </div>
                                <div className="rounded-lg px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                    <div className="text-base font-bold text-white">{pct}%</div>
                                    <div className="text-[10px] text-white/35 mt-0.5">
                                        Conversão
                                    </div>
                                </div>
                                <div className="rounded-lg px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                    <div className="flex items-center justify-center">
                                        <StatusBadge ds={ds} />
                                    </div>
                                    <div className="text-[10px] text-white/35 mt-1">Status</div>
                                </div>
                                <div className="rounded-lg px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                    {ds.bot_ids?.length > 0 ? (
                                        <div className="flex items-center justify-center flex-wrap gap-1">
                                            {ds.bot_ids.map((bid) => {
                                                const bot = bots.find((b) => b.id === bid);
                                                return bot ? (
                                                    <BotAvatar
                                                        key={bid}
                                                        photoUrl={photoByUsername[bot.bot_username]}
                                                        size={22}
                                                        title={bot.bot_username}
                                                    />
                                                ) : null;
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-white/20 text-xs">—</div>
                                    )}
                                    <div className="text-[10px] text-white/35 mt-1">Bots</div>
                                </div>
                            </div>

                            <div className="text-[11px] text-white/25 mb-3">
                                Última execução: {ds.last_run_at ? fmtDate(ds.last_run_at) : '—'}
                            </div>

                            {/* Ações */}
                            <div className="flex items-center gap-2">
                                {!needsCancelFirst && (
                                    <ActionButton
                                        icon={isSending ? Pause : Play}
                                        title={isSending ? "Pausar" : "Iniciar"}
                                        text={isSending ? "Pausar" : "Iniciar"}
                                        onClick={() => isSending ? onStop(ds) : onStart(ds)}
                                        disabled={activeSchedule && !isSending}
                                        variant={isSending ? "action-btn--yellow" : "action-btn--green"}
                                        mobile
                                    />
                                )}
                                {needsCancelFirst ? (
                                    <button
                                        onClick={() => onCancelSchedule(ds)}
                                        disabled={isSending}
                                        className="action-btn action-btn--red action-btn--labeled action-btn--mobile flex-1"
                                        title="Cancelar agendamento"
                                    >
                                        <CalendarX2 size={18} />
                                        <span>Cancelar</span>
                                    </button>
                                ) : (
                                    <ActionButton
                                        icon={CalendarClock}
                                        title="Agendar"
                                        text="Agendar"
                                        onClick={() => onSchedule(ds)}
                                        disabled={isSending}
                                        variant="action-btn--blue"
                                        mobile
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Paginação */}
            {!loading && disparos.length > 0 && pagination && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-white/40">
                        Página {currentPage} de {pagination.pages} ({pagination.total} disparos)
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onPrevPage}
                            disabled={!pagination.has_prev}
                            className="btn-secondary"
                            style={{ opacity: pagination.has_prev ? 1 : 0.5 }}
                        >
                            <i className="fas fa-chevron-left" /> Anterior
                        </button>
                        <button
                            onClick={onNextPage}
                            disabled={!pagination.has_next}
                            className="btn-primary"
                            style={{ opacity: pagination.has_next ? 1 : 0.5 }}
                        >
                            Próximo <i className="fas fa-chevron-right" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
